import React, { useState, useEffect, useRef } from "react";
import { Button, notification } from "antd";
import { ReloadOutlined, CloseOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import VersionService, { UpdateInfo } from "@services/version";
import "./styled.css";

interface UpdateNotificationProps {
  autoCheck?: boolean;
  position?: "top" | "bottom" | "topLeft" | "topRight" | "bottomLeft" | "bottomRight";
}

const UpdateNotification: React.FC<UpdateNotificationProps> = ({ autoCheck = true, position = "topRight" }) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  // Kept in a ref (not state) so destroying/replacing the toast never re-runs the
  // mount effect — a state-backed key in the dep array caused an infinite
  // check→notify→setState→re-check loop (version.json polled every ~100ms) once a
  // new version was deployed and `updateAvailable` stayed permanently true.
  const notificationKeyRef = useRef<string | null>(null);
  // True while an update toast is on screen, so a burst of checks (mount + the
  // app-update-available event firing together) shows exactly one notification.
  const isNotifyingRef = useRef(false);
  const versionService = VersionService.getInstance();

  useEffect(() => {
    const handleUpdateAvailableEvent = () => {
      console.log("[UpdateNotification] Update available event received");
      checkForUpdates();
    };

    // Listen for update events from version service
    window.addEventListener("app-update-available", handleUpdateAvailableEvent);

    // Initial check if autoCheck is enabled
    if (autoCheck) {
      checkForUpdates();
    }

    return () => {
      window.removeEventListener("app-update-available", handleUpdateAvailableEvent);
      if (notificationKeyRef.current) {
        notification.destroy(notificationKeyRef.current);
      }
    };
    // Intentionally depends only on autoCheck — see notificationKeyRef comment above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoCheck]);

  const checkForUpdates = async () => {
    // A toast is already showing for the current update — don't re-check or stack
    // another notification (this is what the periodic 30-min poll would otherwise do).
    if (isNotifyingRef.current) return;
    try {
      setIsLoading(true);
      const updateInfo = await versionService.checkForUpdates();

      if (updateInfo.updateAvailable) {
        showUpdateNotification(updateInfo);
      }
    } catch (error) {
      console.error("[UpdateNotification] Error checking for updates:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const showUpdateNotification = (updateInfo: UpdateInfo) => {
    const key = `update-notification-${Date.now()}`;
    notificationKeyRef.current = key;
    isNotifyingRef.current = true;

    notification.info({
      key,
      message: t("messages.updateAvailable"),
      description: (
        <div className="update-notification-content">
          <p>A new version of the app is available.</p>
          <p>
            <strong>Current:</strong> {updateInfo.currentVersion}
            {updateInfo.newVersion && (
              <>
                <br />
                <strong>Available:</strong> {updateInfo.newVersion}
              </>
            )}
          </p>
          <div className="update-notification-actions">
            <Button type="primary" size="small" icon={<ReloadOutlined />} onClick={handleApplyUpdate} loading={isLoading} className="update-btn">
              {t("buttons.updateNow")}
            </Button>
            <Button size="small" onClick={handleDismiss} icon={<CloseOutlined />} className="dismiss-btn">
              Later
            </Button>
          </div>
        </div>
      ),
      placement: position,
      duration: 0, // Don't auto-close
      closable: false, // Use custom close button
    });
  };

  const handleApplyUpdate = async () => {
    try {
      setIsLoading(true);

      // Show loading notification
      if (notificationKeyRef.current) {
        notification.destroy(notificationKeyRef.current);
      }

      const loadingKey = `update-loading-${Date.now()}`;
      notification.info({
        key: loadingKey,
        message: t("messages.applyingUpdate"),
        description: t("messages.updateInProgress"),
        duration: 0,
        placement: position,
        icon: <ReloadOutlined spin />,
      });

      await versionService.applyUpdate();

      // The page will reload automatically, but in case it doesn't:
      setTimeout(() => {
        notification.destroy(loadingKey);
        window.location.reload();
      }, 3000);
    } catch (error) {
      console.error("[UpdateNotification] Error applying update:", error);
      setIsLoading(false);

      notification.error({
        message: t("messages.updateFailed"),
        description: t("messages.updateFailedDescription"),
        placement: position,
        duration: 5,
      });
      // The apply failed and the original toast was already destroyed above — reset the guard
      // so a future poll/event can re-surface the update toast and the user can retry.
      isNotifyingRef.current = false;
    }
  };

  const handleDismiss = () => {
    if (notificationKeyRef.current) {
      notification.destroy(notificationKeyRef.current);
      notificationKeyRef.current = null;
    }
    // Allow a future check (next 30-min poll or manual) to surface the toast again.
    isNotifyingRef.current = false;
  };

  // Headless component: it only listens for update events and drives the notification toast.
  // Manual "Check for updates" lives in the Settings App Version modal (VersionControl).
  return null;
};

export default UpdateNotification;
